import React from "react";
import { Sparkles } from "lucide-react";

/**
 * Founding Member visual treatment — elegant, subdued gold across The Archive.
 * Founding Member status itself remains Owner-only at the backend; these
 * components are pure presentation.
 *
 * Color system (intentional, not bright yellow):
 *   GOLD       — primary readable text/border (#E0A526)
 *   GOLD_DIM   — softer hairline border  (rgba(224,165,38,0.55))
 *   GOLD_GLOW  — subtle background tint  (rgba(224,165,38,0.06))
 *   GOLD_HALO  — outer ring shadow       (0 0 0 1px rgba(224,165,38,0.30))
 *
 * No animation. No flashy gradients. Premium Archive aesthetic.
 */
export const FOUNDING_GOLD = "#E0A526";
export const FOUNDING_GOLD_DIM = "rgba(224, 165, 38, 0.55)";
export const FOUNDING_GOLD_FAINT = "rgba(224, 165, 38, 0.20)";
export const FOUNDING_GOLD_GLOW_BG = "rgba(224, 165, 38, 0.06)";

export const isFoundingMember = (memberLike) =>
  ((memberLike?.designation || "") + "").toLowerCase() === "founding member";

/**
 * Subtle gold frame applied around an existing card / row / panel when the
 * member is a Founding Member. Spreads as a 1px gold outline + faint gold
 * inner wash. Render this wrapper INSIDE the original container's parent
 * to avoid disrupting its layout.
 */
export const foundingPanelStyle = (active) =>
  active
    ? {
        borderColor: FOUNDING_GOLD_DIM,
        backgroundColor: FOUNDING_GOLD_GLOW_BG,
        boxShadow: "inset 0 0 0 1px rgba(224, 165, 38, 0.10)",
      }
    : null;

/**
 * Compact gold pill — Owner-granted Founding Member badge. Shown on every
 * surface where the member appears. Tiny but unmistakable.
 *
 * Variants:
 *   size="sm" → row / list density (default)
 *   size="md" → dossier headline / profile
 */
export const FoundingBadge = ({ size = "sm", testid, withGrantLine = false }) => {
  const isMd = size === "md";
  const pad = isMd ? "px-2.5 py-1" : "px-2 py-0.5";
  const text = isMd ? "text-[10px]" : "text-[9px]";
  const iconSize = isMd ? 12 : 10;
  return (
    <span
      data-testid={testid || "founding-badge"}
      className={`inline-flex items-center gap-1.5 font-mono ${text} uppercase tracking-[0.3em] border ${pad}`}
      style={{
        color: FOUNDING_GOLD,
        borderColor: FOUNDING_GOLD_DIM,
        backgroundColor: FOUNDING_GOLD_GLOW_BG,
      }}
      title="Founding Member — granted by the Owner of The Archive"
    >
      <Sparkles size={iconSize} />
      <span>FOUNDING MEMBER</span>
      {withGrantLine && (
        <span className="opacity-70 tracking-[0.25em]">· GRANTED BY OWNER</span>
      )}
    </span>
  );
};

/**
 * Gold-tinted callsign text. Use in place of the default `#E4DFD3` color
 * when the member is a Founding Member. Returns the resolved color so
 * callers can plug it into inline `style` or a tailwind helper.
 */
export const foundingCallsignColor = (active, fallback = "#E4DFD3") =>
  active ? FOUNDING_GOLD : fallback;

/**
 * Clearance star color — gold by default for everyone (existing system),
 * but with extra weight when Founding. Returns inline-style props.
 */
export const foundingStarsStyle = (active) =>
  active
    ? { color: FOUNDING_GOLD, textShadow: "0 0 8px rgba(224, 165, 38, 0.30)" }
    : { color: FOUNDING_GOLD };

export default FoundingBadge;
